/* eslint-disable camelcase */
/**
 * @file AppointmentsManagementView.jsx
 * @author German Mogollon
 * @description Appointments Managements View
 */

// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import {
  CalendarOutlined,
  ScheduleFilled,
  EditOutlined,
  HistoryOutlined,
  CloseCircleFilled,
  VideoCameraFilled
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';
import { Row, Col, Modal, Button, Empty } from 'antd';
import moment from 'moment';

// Components
import ButtonFilled from '@nephro/components/ButtonFilled';
import ModalOptions from '@nephro/components/ModalOptions';

// Helpers
import {
  getStorageItem,
  setStorageItem
} from '@nephro/commons/helpers/storage-helper';
import { hasActiveVideoCall } from 'commons/helpers/utils';
import { capitalizeFirstLetter } from '@nephro/commons/helpers/string-helper';
import usePatientIndicators from '../../Indicators/helpers/usePatientIndicators';

// Styles
import styles from './styles.module.css';

const AppointmentsManagementView = ({
  calendarActions,
  appointmentsZoomActions,
  patientActions,
  actions
}) => {
  const [todayQuotes, setTodayQuotes] = React.useState([]);
  const [visible, setVisible] = React.useState(false);
  const [visibleCancel, setVisibleCancel] = React.useState(false);
  const [errorAgendaMate, setErrorAgendaMate] = React.useState(false);
  const [selectedQuote, setSelectedQuote] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [upcomingQuotes, setUpcomingQuotes] = React.useState([]);
  const [pastQuotes, setPastQuotes] = React.useState([]);
  const [userData] = usePatientIndicators(34);
  const { t: translate } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  let activeVideocall = false;

  const { getPatientLastAppointment } = patientActions;
  const { getPatientAppointmentsArray, getAgendaMatePatient } = calendarActions;
  const {
    cancelAppointmentZoom,
    getZoomVideoCallById,
    getMultipleZoomVideoCallById
  } = appointmentsZoomActions;

  const { setLoading } = actions;

  const addZoomMeetingId = (appointments) => {
    getMultipleZoomVideoCallById({ appointments }, (_, zoomResp) => {
      if (zoomResp) {
        const quotes = zoomResp.map((item, index) => {
          const zoomMeetingId = item?.data?.id_zoom_call;
          return { ...appointments[index], zoomMeetingId };
        });
        const today = moment().format('YYYY-MM-DD');
        const todayQts = quotes.filter((itm) => itm.cap_fecha === today);
        const upcomingQts = quotes.filter((itm) => itm.cap_fecha > today);
        setTodayQuotes(todayQts);
        setUpcomingQuotes(upcomingQts);
      }
    });
  };

  // Function to get and set patient appointments list
  const onGetPatientAppointmentsList = async (_, response) => {
    const quotes = response?.data?.data?.quotes;
    if (quotes) {
      await setLoading(true);
      addZoomMeetingId(quotes);
    }
  };

  const onLoadAppointmentList = async (_, response) => {
    if (response?.data) {
      await setLoading(true);
      setIsLoading(true);
      setErrorAgendaMate(true);
      if (response.data.error) {
        setErrorAgendaMate(true);
        Modal.error({
          title: translate('Error al cargar las citas disponibles'),
          content:
            'Se ha presentado un error con el servicio de Agendamate, por favor inténtelo más tarde.'
        });
        setIsLoading(false);
      } else {
        setErrorAgendaMate(false);
        setIsLoading(false);
        const { paciente } = response?.data?.data;
        setStorageItem('agendaMatePatientInfo', paciente);
        const data = { patientId: paciente.id };
        getPatientAppointmentsArray(data, onGetPatientAppointmentsList);
      }
    }
  };

  const onGetAgendaMatePatientInfo = ({
    id_document,
    identification_type: { id_agenda_mate }
  }) => {
    const data = {
      documentNumber: id_document,
      documentTypeId: id_agenda_mate
    };
    getAgendaMatePatient(data, onLoadAppointmentList);
  };

  // Get and set patient info
  const onGetPatientInfo = async (type, response) => {
    if (type === 'success') {
      setPastQuotes(response.data.date_last_appointment);
    }
  };

  React.useEffect(() => {
    const getPatientInfo = async () => {
      const patientInfo = await getStorageItem('patientInfo');
      onGetAgendaMatePatientInfo(patientInfo);
      setIsLoading(true);
      onLoadAppointmentList(patientInfo);
    };
    getPatientInfo();
  }, []);
  React.useEffect(() => {
    const getPatientLastAppointmentInfo = async () => {
      await getPatientLastAppointment(
        { id: userData.paciente_id_versia },
        onGetPatientInfo
      );
    };
    if (userData) {
      getPatientLastAppointmentInfo();
    }
  }, [userData]);

  const onScheduleAppointment = () =>
    history.push(`${location.pathname}/calendar`, null);

  const onReScheduleAppointment = () =>
    history.push(`${location.pathname}/calendar`, selectedQuote);

  const onResult = async (type) => {
    if (type === 'error') {
      Modal.error({
        title: translate('calendarMg.systemMsg'),
        content: 'Error al tratar de cancelar la cita'
      });
    } else {
      const patientInfo = await getStorageItem('patientInfo');
      onGetAgendaMatePatientInfo(patientInfo);
      Modal.success({
        title: translate('calendarMg.systemMsg'),
        content: `Tu cita ${selectedQuote.id} del ${selectedQuote.cap_fecha_show} con el profesional ${selectedQuote.profesional} ha sido cancelada exitosamente.`
      });
    }
  };

  const onGetVideoCallInfo = async (_, resp) => {
    const meetingId = resp?.data?.id_zoom_call || '';
    await setLoading(true);
    const data = { quoteId: selectedQuote.id, meetingId };
    cancelAppointmentZoom(data, onResult);
  };

  const onConfirmCancel = () => {
    setVisibleCancel(false);
    const data = { quoteId: selectedQuote.id };
    getZoomVideoCallById(data, onGetVideoCallInfo);
  };

  const onCancelAppointment = () => {
    setVisible(false);
    setVisibleCancel(true);
  };

  const onSelectQuote = (itm) => {
    setSelectedQuote(itm);
    setVisible(true);
  };

  const onStartVideoCall = (appointment) => {
    const data = { quoteId: appointment.id };
    getZoomVideoCallById(data, (_, resp) => {
      const meetingId = resp?.data?.id_zoom_call;
      history.push('/main/videocalls-frame', { meetingId });
    });
  };

  const dateCard = (itm) => {
    setStorageItem('activeVideocall', '');
    const current = moment.utc(itm?.cap_horainicio);
    const time = {
      cap_fecha: current.format('YYYY-MM-DD'),
      cap_hora: current.format('h:mm a')
    };
    activeVideocall = hasActiveVideoCall(time) && itm?.zoomMeetingId;
    if (activeVideocall) {
      setStorageItem('activeVideocall', itm.id);
    }
    return (
      <div className={styles.appointmentCard}>
        <div className="w-5/6 mb-5">
          <h2 className="mb-0 text-lg font-bold text-baxterGray">
            {itm.tc_tipoconsulta} - {itm.modalidad}
            {itm?.zoomMeetingId && (
              <span className="text-brandNavy ml-2">(NefroPlus)</span>
            )}
          </h2>
          <h2 className="mb-0 text-baxterGray text-lg">{itm.profesional}</h2>
          <h2 className="mb-0 text-baxterGray text-lg">
            {moment(time.cap_fecha).format('D MMM, YYYY')} - {time.cap_hora}
          </h2>
          <h2 className="mb-0 text-baxterGray text-lg">{itm.cr_direccion}</h2>
        </div>
        {!itm?.past && (
          <div
            onClick={() => onSelectQuote(itm)}
            className="cursor-pointer p-2 w-1/6 text-right">
            <EditOutlined style={{ color: 'var(--color-grayBorders)' }} />
          </div>
        )}
        {activeVideocall && (
          <ButtonFilled
            onActionClick={() => onStartVideoCall(itm)}
            type="green"
            textButton={translate('calendarMg.startVideoCall')}
            className="w-full text-center"
            iconComponent={<VideoCameraFilled style={{ marginTop: -2 }} />}
          />
        )}
      </div>
    );
  };

  const modalOptions = [
    {
      icon: <HistoryOutlined style={{ color: 'var(--color-primary)' }} />,
      textOption: 'Reagendar cita',
      type: 'primary',
      onClick: () => onReScheduleAppointment()
    },
    {
      icon: <CloseCircleFilled style={{ color: 'var(--color-error)' }} />,
      textOption: 'Cancelar cita',
      type: 'error',
      onClick: () => onCancelAppointment()
    }
  ];

  const emptyValidation =
    !upcomingQuotes.length && !todayQuotes.length && !pastQuotes;
  return (
    <div className="h-full p-5">
      <Modal visible={visibleCancel} title="" closable={false} footer={null}>
        <div className="text-center">
          <CloseCircleFilled
            style={{ color: 'var(--color-baxter-teal-medium)', fontSize: 88 }}
          />
          <h2 className="mb-0 mt-4 text-2xl font-bold text-baxterGray">
            {translate('calendarMg.confirmInfo')}
          </h2>
          <h2 className="mb-0 text-lg text-baxterGray">
            {selectedQuote?.tc_tipoconsulta}
          </h2>
          <h2 className="mb-0 text-baxterGray text-base">
            {selectedQuote?.profesional}
          </h2>
          <h2 className="mb-0 text-baxterGray text-base">
            {capitalizeFirstLetter(
              moment(selectedQuote?.cap_fecha).format('MMMM D, YYYY')
            )}{' '}
            - {moment(selectedQuote?.cap_hora, 'HH:mm').format('h:mm a')}
          </h2>
          <h2 className="mb-0 text-baxterGray text-base">
            {selectedQuote?.cr_direccion}
          </h2>
          <div className="w-full flex flex-row justify-center mt-6">
            <Button
              className={styles.cancelButton}
              onClick={() => setVisibleCancel(false)}>
              {translate('calendarMg.close')}
            </Button>
            <Button
              className={styles.okButton}
              onClick={() => onConfirmCancel()}>
              {translate('calendarMg.cancelQuote')}
            </Button>
          </div>
        </div>
      </Modal>
      <ModalOptions
        modalOptions={modalOptions}
        visible={visible}
        setVisible={setVisible}
      />
      <div className="flex flex-row items-center text-baxterGray mb-5">
        <ScheduleFilled
          size={40}
          style={{ fontSize: 40, color: 'var(--color-baxter-gray)' }}
        />
        <h2 className="mb-0 ml-3 text-xl text-baxterGray font-bold">
          {translate('calendarMg.myAppoint')}
        </h2>
      </div>
      {!errorAgendaMate && !isLoading && (
        <ButtonFilled
          onActionClick={onScheduleAppointment}
          type="cyan"
          textButton={translate('calendarMg.newDate')}
          classStyles={[styles.actionButtonAppoint].join(' ')}
          iconComponent={<CalendarOutlined style={{ marginTop: -2 }} />}
        />
      )}
      {errorAgendaMate && (
        <div>
          <Empty
            style={{ color: '#858fab', fontSize: 15, marginTop: 160 }}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <h3 className="text-center text-base font-bold text-placeholder">
                {translate('commons.errorAgendamate')}
              </h3>
            }
          />
          <h3 className="text-center -mt-8 text-base text-placeholder">
            {translate('commons.tryLaterAgendamate')}
          </h3>
        </div>
      )}
      {emptyValidation && !errorAgendaMate && !isLoading && (
        <div>
          <Empty
            style={{ color: '#858fab', fontSize: 15, marginTop: 160 }}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <h3 className="text-center text-base font-bold text-placeholder">
                {translate('commons.noAppointments')}
              </h3>
            }
          />
          <h3 className="text-center -mt-8 text-base text-placeholder">
            {translate('commons.appointSubtitle')}
          </h3>
        </div>
      )}
      {todayQuotes.length > 0 && (
        <>
          <h2 className="mb-3 mt-7 text-lg font-bold text-baxterGray">
            {translate('calendarMg.today')}
          </h2>
          <Row gutter={[7, 7]}>
            {todayQuotes.map((itm) => {
              return (
                <Col key={itm.id} xs={24} sm={12} md={12} lg={8} xl={8}>
                  {dateCard(itm)}
                </Col>
              );
            })}
          </Row>
        </>
      )}
      {upcomingQuotes.length > 0 && (
        <>
          <h2 className="mb-3 mt-7 text-lg font-bold text-baxterGray">
            {translate('calendarMg.upcomming')}
          </h2>
          <Row gutter={[7, 7]}>
            {upcomingQuotes.map((itm) => {
              return (
                <Col key={itm.id} xs={24} sm={12} md={8} lg={8} xl={8}>
                  {dateCard(itm)}
                </Col>
              );
            })}
          </Row>
        </>
      )}
      {pastQuotes && (
        <>
          <h2 className="mb-3 mt-7 text-lg font-bold text-baxterGray">
            {translate('calendarMg.past')}
          </h2>
          <Row gutter={[7, 7]}>
            <Col xs={24} sm={12} md={8} lg={8} xl={8}>
              {dateCard({
                cap_horainicio: pastQuotes,
                tc_tipoconsulta: 'CONSULTA INTEGRAL DE CONTROL',
                profesional: 'Equipo interdisciplinario',
                past: true
              })}
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

/**
 * Component definition of proptypes.
 */
AppointmentsManagementView.propTypes = {
  actions: PropTypes.shape({
    setLoading: PropTypes.func.isRequired
  }).isRequired,
  calendarActions: PropTypes.shape({
    getPatientAppointmentsArray: PropTypes.func.isRequired,
    getAgendaMatePatient: PropTypes.func.isRequired
  }).isRequired,
  appointmentsZoomActions: PropTypes.shape({
    cancelAppointmentZoom: PropTypes.func.isRequired,
    getZoomVideoCallById: PropTypes.func.isRequired,
    getMultipleZoomVideoCallById: PropTypes.func.isRequired
  }).isRequired,
  patientActions: PropTypes.shape({
    getPatientLastAppointment: PropTypes.func.isRequired
  }).isRequired,
  loading: PropTypes.bool
};

export default AppointmentsManagementView;
